import { OfflineDatabase, OfflineStoreConfig } from '../types/database.types'

export class OfflineDatabaseManager {
    private db: IDBDatabase | null = null
    private config: OfflineStoreConfig

    constructor(config: OfflineStoreConfig) {
        this.config = config
    }

    async initialize(): Promise<void> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.config.databaseName, this.config.version)

            request.onerror = () => {
                reject(new Error('Failed to open database'))
            }

            request.onsuccess = (event) => {
                this.db = (event.target as IDBOpenDBRequest).result
                resolve()
            }

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result

                // Create object stores
                if (!db.objectStoreNames.contains('patients')) {
                    const patientStore = db.createObjectStore('patients', { keyPath: '_id' })
                    patientStore.createIndex('_offlineId', '_offlineId', { unique: true })
                    patientStore.createIndex('_syncStatus', '_syncStatus', { unique: false })
                }

                if (!db.objectStoreNames.contains('consultations')) {
                    const consultationStore = db.createObjectStore('consultations', { keyPath: '_id' })
                    consultationStore.createIndex('_offlineId', '_offlineId', { unique: true })
                    consultationStore.createIndex('_syncStatus', '_syncStatus', { unique: false })
                    consultationStore.createIndex('patientId', 'patient._id', { unique: false })
                }

                if (!db.objectStoreNames.contains('documents')) {
                    const documentStore = db.createObjectStore('documents', { keyPath: '_id' })
                    documentStore.createIndex('_offlineId', '_offlineId', { unique: true })
                    documentStore.createIndex('_syncStatus', '_syncStatus', { unique: false })
                    documentStore.createIndex('objectId', 'objectId', { unique: false })
                }

                if (!db.objectStoreNames.contains('formSubmissions')) {
                    const formSubmissionStore = db.createObjectStore('formSubmissions', { keyPath: '_id' })
                    formSubmissionStore.createIndex('_offlineId', '_offlineId', { unique: true })
                    formSubmissionStore.createIndex('_syncStatus', '_syncStatus', { unique: false })
                    formSubmissionStore.createIndex('consultationId', 'consultationId', { unique: false })
                }

                if (!db.objectStoreNames.contains('transcriptions')) {
                    const transcriptionStore = db.createObjectStore('transcriptions', { keyPath: '_id' })
                    transcriptionStore.createIndex('_offlineId', '_offlineId', { unique: true })
                    transcriptionStore.createIndex('_syncStatus', '_syncStatus', { unique: false })
                    transcriptionStore.createIndex('consultationId', 'consultationId', { unique: false })
                }

                if (!db.objectStoreNames.contains('biologicals')) {
                    const biologicalStore = db.createObjectStore('biologicals', { keyPath: '_id' })
                    biologicalStore.createIndex('_offlineId', '_offlineId', { unique: true })
                    biologicalStore.createIndex('_syncStatus', '_syncStatus', { unique: false })
                    biologicalStore.createIndex('consultationId', 'consultationId', { unique: false })
                }

                if (!db.objectStoreNames.contains('vitalParameters')) {
                    const vitalParameterStore = db.createObjectStore('vitalParameters', { keyPath: '_id' })
                    vitalParameterStore.createIndex('_offlineId', '_offlineId', { unique: true })
                    vitalParameterStore.createIndex('_syncStatus', '_syncStatus', { unique: false })
                    vitalParameterStore.createIndex('consultationId', 'consultationId', { unique: false })
                }

                if (!db.objectStoreNames.contains('syncQueue')) {
                    const syncQueueStore = db.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: true })
                    syncQueueStore.createIndex('status', 'status', { unique: false })
                    syncQueueStore.createIndex('entityType', 'entityType', { unique: false })
                    syncQueueStore.createIndex('timestamp', 'timestamp', { unique: false })
                }

                if (!db.objectStoreNames.contains('conflicts')) {
                    const conflictsStore = db.createObjectStore('conflicts', { keyPath: 'id' })
                    conflictsStore.createIndex('entityType', 'entityType', { unique: false })
                    conflictsStore.createIndex('conflictType', 'conflictType', { unique: false })
                    conflictsStore.createIndex('createdAt', 'createdAt', { unique: false })
                }
            }
        })
    }

    async close(): Promise<void> {
        if (this.db) {
            this.db.close()
            this.db = null
        }
    }

    getDatabase(): IDBDatabase {
        if (!this.db) {
            throw new Error('Database not initialized')
        }
        return this.db
    }
}

// Create a singleton instance
const defaultConfig: OfflineStoreConfig = {
    databaseName: 'telemed-offline',
    version: 2, // Incremented to include conflicts store
}

export const offlineDatabase = new OfflineDatabaseManager(defaultConfig) 